import React, { useEffect, useCallback, useMemo } from 'react';
import { ChatProvider, useChatContext } from '../bot-core/store/ChatContext';
import { BotExtProvider, useBotContext } from './store/BotContext';
import { useBotSession } from './hooks/useBotSession';
import { useChatSocket } from '../bot-core/hooks/useChatSocket';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import ChatButton from '../bot-core/ChatButton';
import ChatPanel from '../bot-core/ChatPanel';
import ChatHeader from '../bot-core/ChatHeader';
import ChatMessages from '../bot-core/ChatMessages';
import ChatInput from '../bot-core/ChatInput';
import ItinerarySummaryCard from './ItinerarySummaryCard';
import CartPreviewCard from './CartPreviewCard';
import CheckoutPanel from './CheckoutPanel';
import CheckoutCtaButton from './CheckoutCtaButton';
import SearchResultList from './search-results/SearchResultList';
import RoomOptionsList from './search-results/RoomOptionsList';
import { UIMessage } from '../bot-core/types/chat.types';
import { IoBagHandleOutline } from 'react-icons/io5';
import { Tabs } from 'antd';
import {
  useBotCheckoutHandler,
  extractBotSessionId,
} from './hooks/useBotCheckoutHandler';
import { useWhatsappHandoff } from './hooks/useWhatsappHandoff';
import type { WhatsappAvailability } from './hooks/useBotAgentAvailability';
import { FaWhatsapp } from 'react-icons/fa';
import '../bot-core/chat.less';
import './bot.less';

const TOOL_TO_PRODUCT: Record<string, 'flight' | 'hotel' | 'package' | 'activity' | 'event'> = {
  search_flights: 'flight',
  search_hotels: 'hotel',
  search_packages: 'package',
  search_attractions: 'activity',
  search_events: 'event',
};

interface BotWidgetInnerProps {
  whatsapp?: WhatsappAvailability | null;
}

const BotWidgetInner: React.FC<BotWidgetInnerProps> = ({ whatsapp }) => {
  const { state: chatState, dispatch: chatDispatch } = useChatContext();
  const { botState, botDispatch } = useBotContext();
  const {
    initSession,
    refreshSelections,
    fetchCheckout,
    tabs,
    activeTabId,
    createTab,
    switchTab,
    closeTab,
  } = useBotSession();

  const t = useSelector((s: RootState) => (s.config.translations as any)?.bot);
  const tenantId = Number(process.env.REACT_APP_TENANT_ID) || undefined;

  const onDone = useCallback(async (meta: any) => {
    if (!botState.sessionId) return;
    await refreshSelections(botState.sessionId);
    // Fetch the checkout payload after either tool — initiate_checkout (cart
    // review) or generate_checkout_url (final URL). The latter is the path
    // the single-step prompt uses; the former is only fired when the user
    // explicitly asks for a cart review.
    if (
      meta?.lastTool === 'initiate_checkout' ||
      meta?.lastTool === 'generate_checkout_url'
    ) {
      await fetchCheckout(botState.sessionId);
    }
  }, [botState.sessionId, refreshSelections, fetchCheckout]);

  const onHandedOff = useCallback(
    (handedSessionId: string) => {
      if (handedSessionId === botState.sessionId) {
        chatDispatch({ type: 'SET_OPEN', isOpen: false });
        chatDispatch({
          type: 'ADD_MESSAGE',
          message: {
            id: crypto.randomUUID(),
            role: 'system',
            // TODO(i18n): translation key bot.handoff.handedOff
            text: 'This conversation continues on WhatsApp.',
            timestamp: new Date().toISOString(),
          } as UIMessage,
        });
      }
    },
    [botState.sessionId, chatDispatch],
  );

  const { connect, disconnect, emit } = useChatSocket({
    namespace: '/bot-agent',
    onDone,
    onHandedOff,
  });

  const { handoff: triggerWhatsappHandoff, busy: handoffBusy } =
    useWhatsappHandoff();

  useEffect(() => { initSession(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (chatState.isOpen && botState.sessionId) connect();
    if (!chatState.isOpen) disconnect();
  }, [chatState.isOpen, botState.sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = () => chatDispatch({ type: 'SET_OPEN', isOpen: !chatState.isOpen });
  const handleClose = () => chatDispatch({ type: 'SET_OPEN', isOpen: false });

  const handleSend = (
    text: string,
    opts?: { selection?: { book_hash?: string } },
  ) => {
    if (!botState.sessionId) return;
    emit('send_message', {
      session_id: botState.sessionId,
      text,
      ...(tenantId ? { tenant_id: tenantId } : {}),
      // Structured room selection rides alongside the friendly text so the BE
      // can hand the LLM the exact book_hash (see BotAgentService selection hint).
      ...(opts?.selection ? { selection: opts.selection } : {}),
    });
  };

  const isBlocked = botState.sessionStatus === 'blocked';
  const hasActivity = chatState.messages.length > 0 || botState.selections.length > 0;

  const handleRemoveItem = (displayName: string) => {
    handleSend(`Remove ${displayName}`);
  };

  // Intercepts the bot-generated checkout link (`/shopping-cart?bot_session_id=…`).
  // Builds the V2 cart from BE, stashes to localStorage in the shape the
  // existing Checkout page expects, then navigates to `/checkout/<uuid>`.
  // Non-bot links fall through to the default react-router / `<a>` behaviour.
  const handleBotCheckout = useBotCheckoutHandler();
  const onLinkClick = useCallback(
    (url: string, _e: React.MouseEvent): boolean => {
      const sid = extractBotSessionId(url);
      if (!sid) return false;
      handleBotCheckout(sid);
      return true;
    },
    [handleBotCheckout],
  );

  const lastMsgId = chatState.messages[chatState.messages.length - 1]?.id;
  const renderMessageExtra = (msg: UIMessage) => {
    const extras: React.ReactNode[] = [];
    if (msg.searchResults && msg.id === lastMsgId && !chatState.isStreaming) {
      const productType = TOOL_TO_PRODUCT[msg.searchResults.tool] ?? 'hotel';
      extras.push(
        <SearchResultList
          key="search-results"
          productType={productType}
          results={msg.searchResults.results}
          items={msg.searchResults.items}
          onSelect={(index, displayName) => {
            handleSend(`I'll take option ${index + 1} — ${displayName}`);
          }}
        />,
      );
    }
    // Hotel room options (view_product_details). Gated to the last message +
    // not-streaming (same as searchResults), and only when the sideband carries
    // hotel pricing — other product types keep their LLM-text-only behaviour.
    if (
      msg.productDetails?.raw?.pricing &&
      msg.id === lastMsgId &&
      !chatState.isStreaming
    ) {
      extras.push(
        <RoomOptionsList
          key="room-options"
          raw={msg.productDetails.raw}
          onSelect={(bookHash, friendlyText) => {
            handleSend(friendlyText, { selection: { book_hash: bookHash } });
          }}
        />,
      );
    }
    if (msg.cartPreview && !chatState.isStreaming) {
      extras.push(<CartPreviewCard key="cart-preview" cartPreview={msg.cartPreview} />);
    }
    // Structured checkout CTA — rendered from the generate_checkout_url sideband,
    // NOT scraped from the LLM prose. Tapping it runs the single web checkout
    // handler (→ /checkout/<uuid>). This is the reliable entry point.
    if (msg.checkoutReady && !chatState.isStreaming) {
      extras.push(
        <CheckoutCtaButton
          key="checkout-cta"
          sessionId={msg.checkoutReady.session_id}
          expiresAt={msg.checkoutReady.expires_at}
          onCheckout={handleBotCheckout}
          label={t?.checkout?.completeBooking}
        />,
      );
    }
    return extras.length ? <>{extras}</> : null;
  };

  // "Preview cart" chip lives in the header for the ACTIVE tab — single
  // chip across the strip per the agreed UX. Toggles panel via SHOW/HIDE.
  // Always visible while there's a payload, regardless of panel state, so
  // it remains a discoverable indicator that the active tab has a checkout.
  const hasCheckoutPayload = !!botState.checkoutPayload;
  const showWhatsappHandoff =
    !!whatsapp?.enabled &&
    !!whatsapp?.business_number &&
    !!botState.sessionId;

  const headerActions = (
    <>
      {showWhatsappHandoff && (
        <button
          className="bot-icon-btn"
          // TODO(i18n): translation key bot.handoff.continueOnWhatsapp
          title="Continue on WhatsApp"
          disabled={handoffBusy}
          onClick={() => {
            if (botState.sessionId) triggerWhatsappHandoff(botState.sessionId);
          }}
        >
          <FaWhatsapp size={20} />
        </button>
      )}
      {hasCheckoutPayload && (
        <button
          className={`bot-icon-btn${botState.checkoutReady ? ' bot-icon-btn--active' : ''}`}
          title={t?.previewCart ?? 'Preview cart'}
          onClick={() =>
            botDispatch({
              type: botState.checkoutReady ? 'HIDE_CHECKOUT' : 'SHOW_CHECKOUT',
            })
          }
        >
          <IoBagHandleOutline size={20} />
        </button>
      )}
    </>
  );

  // Whenever the active session shows its first user message, cache it as
  // the label so the tab strip keeps it after switching away (Redux-backed
  // `tabTitles` map — see BotContext.SET_TAB_TITLE).
  const firstUserText = useMemo(() => {
    const m = chatState.messages.find((m) => m.role === 'user');
    return m?.text;
  }, [chatState.messages]);
  useEffect(() => {
    if (activeTabId && firstUserText) {
      botDispatch({
        type: 'SET_TAB_TITLE',
        sessionId: activeTabId,
        title: firstUserText.slice(0, 40),
      });
    }
  }, [activeTabId, firstUserText, botDispatch]);

  // Browser-style tabs in the chat header. Label resolution order:
  //   1. botState.tabTitles[id] (persistent cache, survives switches)
  //   2. tab.title (snapshot at create time)
  //   3. localized "New chat" placeholder
  const newChatLabel = t?.newChat ?? 'New chat';
  const tabItems = tabs.map((tab) => ({
    key: tab.id,
    label:
      (botState.tabTitles[tab.id] && botState.tabTitles[tab.id].trim()) ||
      (tab.title && tab.title.trim()) ||
      newChatLabel,
    // The chat panel itself is rendered ONCE outside the Tabs container —
    // tab switching reloads the active session into that single panel.
    // Using `children` here just satisfies AntD's API.
    children: null,
    closable: true,
  }));

  const tabsStrip = tabs.length > 0 ? (
    <div className="bot-tabs-strip">
      <Tabs
        type="editable-card"
        activeKey={activeTabId ?? undefined}
        items={tabItems}
        hideAdd={false}
        onChange={(key) => switchTab(key)}
        onEdit={(targetKey, action) => {
          if (action === 'add') {
            createTab();
          } else if (action === 'remove' && typeof targetKey === 'string') {
            closeTab(targetKey);
          }
        }}
      />
    </div>
  ) : null;

  return (
    <>
      <ChatButton isOpen={chatState.isOpen} onClick={handleToggle} hasActivity={hasActivity} />
      {chatState.isOpen && (
        <ChatPanel
          header={
            <>
              <ChatHeader
                title={t?.title ?? 'Sky360 Travel Assistant'}
                avatarEmoji="✈️"
                extraActions={headerActions}
                onClose={handleClose}
              />
              {tabsStrip}
            </>
          }
          body={
            <ChatMessages
              avatarEmoji="✈️"
              isBlocked={isBlocked}
              emptyStateContent={
                <div className="bot-empty-state">
                  <div className="bot-empty-state__icon">✈️</div>
                  <div className="bot-empty-state__title">{t?.emptyState?.title ?? 'How can I help plan your trip?'}</div>
                  <div className="bot-empty-state__sub">{t?.emptyState?.subtitle ?? 'Ask me about flights, hotels, packages, or activities.'}</div>
                </div>
              }
              blockedMessage={t?.blocked ?? 'This chat has been disabled.'}
              extraBottom={botState.selections.length > 0 ? <ItinerarySummaryCard onRemove={handleRemoveItem} /> : null}
              renderMessageExtra={renderMessageExtra}
              onLinkClick={onLinkClick}
            />
          }
          footer={
            <ChatInput
              onSend={handleSend}
              isBlocked={isBlocked}
              placeholder={t?.input?.placeholder ?? 'Ask me anything about your trip\u2026'}
            />
          }
          overlay={botState.checkoutReady ? <CheckoutPanel /> : undefined}
        />
      )}
    </>
  );
};

interface BotWidgetProps {
  whatsapp?: WhatsappAvailability | null;
}

const BotWidget: React.FC<BotWidgetProps> = ({ whatsapp }) => (
  <ChatProvider>
    <BotExtProvider>
      <BotWidgetInner whatsapp={whatsapp} />
    </BotExtProvider>
  </ChatProvider>
);

export default BotWidget;
